import { zodResolver } from '@hookform/resolvers/zod';
import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { enqueueSnackbar } from 'notistack';

import { useManagePeriodReportModalStore } from './ManagePeriodReportModalStore';

import { CreatePeriodReportFormValues, createPeriodReportSchema } from 'api/periodReports/type';
import { MLInput } from 'components/Input/Input';
import useCreatePeriodReport from 'hooks/PeriodReports/useCreatePeriodReport';

interface CreatePeriodReportModalProps {
  refetchPeriodReports: () => void;
}

const CreatePeriodReportModal: FC<CreatePeriodReportModalProps> = ({ refetchPeriodReports }) => {
  const { createPeriodReport } = useCreatePeriodReport();

  const { slug, isOpen, onManagePeriodVisitModalClose } = useManagePeriodReportModalStore();

  const { t } = useTranslation();
  const { handleSubmit, control } = useForm<CreatePeriodReportFormValues>({
    resolver: zodResolver(createPeriodReportSchema),
    defaultValues: {
      start_date: '',
      end_date: '',
    },
  });

  const onCreatePeriodReport = (data: CreatePeriodReportFormValues) => {
    createPeriodReport(
      { createPeriodReportData: data, slug: slug || '' },
      {
        onSuccess: () => {
          enqueueSnackbar('Period report was created successfully', { variant: 'success' });
          refetchPeriodReports();
          onManagePeriodVisitModalClose();
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onManagePeriodVisitModalClose} size="md">
      <ModalHeader className="border-bottom pb-2 pt-3 mx-3" toggle={onManagePeriodVisitModalClose}>
        Create period report
      </ModalHeader>
      <ModalBody>
        <div className="row m-2">
          <div className="col-md-6">
            <Controller
              control={control}
              name="start_date"
              render={({ field, fieldState: { error } }) => (
                <MLInput type="date" label="Start date" error={error?.message} required {...field} />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              control={control}
              name="end_date"
              render={({ field, fieldState: { error } }) => (
                <MLInput type="date" label="End date" error={error?.message} required {...field} />
              )}
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          className="btn btn-ghost-danger"
          onClick={() => {
            onManagePeriodVisitModalClose();
          }}
        >
          {t('Modals.delete-modal.cancel')}
        </Button>{' '}
        <Button className="btn btn-ghost-success" onClick={handleSubmit(onCreatePeriodReport)}>
          Create report
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreatePeriodReportModal;
